import React, { useState } from 'react';
import {
  ArrowRight, ArrowLeft, CheckCircle, Sparkles,
  Globe, Smartphone, Cloud, Bot, Server, Building2,
  Rocket, Users, MessageSquare, Mail, User, Phone, AlertCircle
} from 'lucide-react';

// ─── EmailJS config — replace with your own from emailjs.com (free tier) ────
const EMAILJS_SERVICE_ID  = 'service_3gztqr7';   // e.g. 'service_abc123'
const EMAILJS_TEMPLATE_ID = 'template_7788ker';  // e.g. 'template_xyz789'
const EMAILJS_PUBLIC_KEY  = '8TKrU3zoDcCuB_rSU';   // Account → API Keys

// ─── EmailJS sender (no backend, free 200/month) ─────────────────────────────
async function sendViaEmailJS(data) {
  // Lazy-load EmailJS SDK from CDN
  if (!window.emailjs) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@4/dist/email.min.js';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
    window.emailjs.init({ publicKey: EMAILJS_PUBLIC_KEY });
  }

  const services = data.services.join(', ') || 'Not specified';

  // These keys must match {{variable_name}} in your EmailJS template
  const templateParams = {
    to_email:    'contact@nuageit.in',
    from_name:   data.name,
    from_email:  data.email,
    phone:       data.phone       || 'Not provided',
    company:     data.company     || 'Not provided',
    services,
    stage:       data.stage       || 'Not specified',
    budget:      data.budget      || 'Not specified',
    timeline:    data.timeline    || 'Not specified',
    description: data.description || 'Not provided',
    reply_to:    data.email,
  };

  const res = await window.emailjs.send(
    EMAILJS_SERVICE_ID,
    EMAILJS_TEMPLATE_ID,
    templateParams
  );

  if (res.status !== 200) throw new Error('EmailJS send failed');
  return true;
}

// ─── Fallback: open mail client pre-filled ───────────────────────────────────
function mailtoFallback(data) {
  const services = data.services.join(', ') || 'Not specified';
  const body = [
    'NEW PROJECT ENQUIRY — NUAGE',
    '===========================',
    '',
    'CONTACT DETAILS',
    '---------------',
    `Name:     ${data.name}`,
    `Email:    ${data.email}`,
    `Phone:    ${data.phone    || 'Not provided'}`,
    `Company:  ${data.company  || 'Not provided'}`,
    '',
    'PROJECT BRIEF',
    '-------------',
    `Services needed: ${services}`,
    `Project stage:   ${data.stage    || 'Not specified'}`,
    `Budget:          ${data.budget   || 'Not specified'}`,
    `Timeline:        ${data.timeline || 'Not specified'}`,
    '',
    'PROJECT DESCRIPTION',
    '-------------------',
    data.description || 'Not provided',
    '',
    'Submitted via nuageit.in contact form',
  ].join('\n');

  window.location.href =
    `mailto:contact@nuageit.in` +
    `?subject=${encodeURIComponent(`New Project Enquiry from ${data.name} — ${services}`)}` +
    `&body=${encodeURIComponent(body)}`;
}

// ─── Form data ────────────────────────────────────────────────────────────────
const SERVICE_OPTIONS = [
  { id: 'web',       icon: <Globe size={20} />,      label: 'Web Development',    desc: 'React, Next.js, MERN, enterprise apps' },
  { id: 'mobile',    icon: <Smartphone size={20} />, label: 'Mobile App',         desc: 'Flutter, React Native, iOS/Android' },
  { id: 'devops',    icon: <Cloud size={20} />,      label: 'DevOps & Cloud',     desc: 'AWS, Azure, CI/CD, Kubernetes' },
  { id: 'agentic',   icon: <Bot size={20} />,        label: 'Agentic AI',         desc: 'Autonomous agents, multi-agent systems' },
  { id: 'genai',     icon: <Sparkles size={20} />,   label: 'Generative AI',      desc: 'LLMs, RAG, chatbots, automation' },
  { id: 'cloud-arch',icon: <Server size={20} />,     label: 'Cloud Architecture', desc: 'Design, migration, cost optimisation' },
];

const STAGE_OPTIONS = [
  { id: 'idea',       icon: <Sparkles size={16} />,   label: 'Just an idea',     desc: "I know what I want to build, haven't started" },
  { id: 'mvp',        icon: <Rocket size={16} />,     label: 'MVP / Prototype',  desc: 'Need a working product fast' },
  { id: 'existing',   icon: <Building2 size={16} />,  label: 'Existing product', desc: 'Improve, scale, or add features' },
  { id: 'enterprise', icon: <Users size={16} />,      label: 'Enterprise project',desc: 'Large-scale system or migration' },
];

const BUDGET_OPTIONS   = [
  { id: 'u5k',     label: 'Under $5K'   },
  { id: '5-15k',   label: '$5K–$15K'   },
  { id: '15-50k',  label: '$15K–$50K'  },
  { id: '50k+',    label: '$50K+'       },
  { id: 'discuss', label: 'Let\'s discuss' },
];

const TIMELINE_OPTIONS = [
  { id: 'asap',     label: 'ASAP'            },
  { id: '1m',       label: 'Within 1 month'  },
  { id: '3m',       label: '1–3 months'      },
  { id: '6m',       label: '3–6 months'      },
  { id: 'flexible', label: 'Flexible'         },
];

const PLACEHOLDER = `Example:
We're building a SaaS dashboard for logistics companies. Users need real-time shipment tracking, custom reports, and integration with our existing REST API.

Figma designs are ready. We need an MVP in 3 months and need to handle 500+ concurrent users.`;

// ─── Small reusable components ────────────────────────────────────────────────

const inputBase = {
  width: '100%', padding: '0.8rem 1rem',
  background: 'rgba(37,138,203,0.04)', border: '1px solid var(--border)',
  borderRadius: '10px', color: 'var(--text-primary)',
  fontFamily: 'var(--font-body)', fontSize: '0.92rem', outline: 'none',
  transition: 'border-color 0.2s',
};

function Label({ icon, children }) {
  return (
    <div style={{ fontSize: '0.86rem', fontWeight: 700, color: 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: '0.35rem', marginBottom: '0.3rem' }}>
      {icon && <span>{icon}</span>}{children}
    </div>
  );
}
function Hint({ children }) {
  return <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '0.6rem', lineHeight: 1.5 }}>{children}</p>;
}
function Req() {
  return <span style={{ color: '#f87171', fontSize: '0.8rem', marginLeft: '2px' }}>*</span>;
}

function Chip({ selected, onClick, children }) {
  return (
    <button onClick={onClick} type="button" style={{
      padding: '0.4rem 0.95rem', borderRadius: '100px',
      fontFamily: 'var(--font-body)', fontSize: '0.82rem', fontWeight: 500,
      cursor: 'pointer', transition: 'all 0.2s',
      background: selected ? 'rgba(37,138,203,0.18)' : 'transparent',
      color: selected ? '#3BA3E8' : 'var(--text-secondary)',
      border: selected ? '1px solid #258ACB' : '1px solid var(--border)',
    }}>{children}</button>
  );
}

function ServiceCard({ opt, selected, onToggle }) {
  return (
    <button onClick={onToggle} type="button" style={{
      display: 'flex', flexDirection: 'column', gap: '0.35rem',
      padding: '1rem 1.15rem', borderRadius: '12px',
      cursor: 'pointer', transition: 'all 0.22s', textAlign: 'left',
      background: selected ? 'rgba(37,138,203,0.1)' : 'rgba(37,138,203,0.03)',
      border: selected ? '1.5px solid #258ACB' : '1px solid var(--border)',
      boxShadow: selected ? '0 0 16px rgba(37,138,203,0.18)' : 'none',
      transform: selected ? 'translateY(-2px)' : 'none',
    }}>
      <div style={{ color: selected ? '#258ACB' : 'var(--text-muted)', transition: 'color 0.2s' }}>{opt.icon}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontSize: '0.88rem', fontWeight: 700, color: 'var(--text-primary)' }}>{opt.label}</div>
      <div style={{ fontSize: '0.73rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>{opt.desc}</div>
    </button>
  );
}

function StageCard({ opt, selected, onClick }) {
  return (
    <button onClick={onClick} type="button" style={{
      display: 'flex', alignItems: 'flex-start', gap: '0.85rem',
      padding: '0.9rem 1.1rem', borderRadius: '12px',
      cursor: 'pointer', transition: 'all 0.22s', textAlign: 'left',
      background: selected ? 'rgba(37,138,203,0.1)' : 'rgba(37,138,203,0.03)',
      border: selected ? '1.5px solid #258ACB' : '1px solid var(--border)',
      boxShadow: selected ? '0 0 14px rgba(37,138,203,0.15)' : 'none',
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: '8px', flexShrink: 0,
        background: selected ? 'rgba(37,138,203,0.2)' : 'rgba(37,138,203,0.06)',
        border: `1px solid ${selected ? '#258ACB' : 'var(--border)'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: selected ? '#258ACB' : 'var(--text-muted)', transition: 'all 0.2s',
      }}>{opt.icon}</div>
      <div>
        <div style={{ fontSize: '0.88rem', fontWeight: 700, fontFamily: 'var(--font-display)', color: 'var(--text-primary)' }}>{opt.label}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', lineHeight: 1.4, marginTop: '0.15rem' }}>{opt.desc}</div>
      </div>
    </button>
  );
}

function ProgressBar({ step }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2.25rem' }}>
      {[
        { n: 1, label: 'Project Brief' },
        { n: 2, label: 'Your Details'  },
      ].map(({ n, label }, idx) => (
        <React.Fragment key={n}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.45rem' }}>
            <div style={{
              width: 26, height: 26, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.72rem', fontWeight: 800, transition: 'all 0.3s',
              background: step >= n ? '#258ACB' : 'rgba(37,138,203,0.08)',
              border: step >= n ? '2px solid #258ACB' : '1px solid var(--border)',
              color: step >= n ? 'white' : 'var(--text-muted)',
              boxShadow: step >= n ? '0 0 12px rgba(37,138,203,0.4)' : 'none',
            }}>{n}</div>
            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: step >= n ? 'var(--text-primary)' : 'var(--text-muted)', transition: 'color 0.3s' }}>
              {label}
            </span>
          </div>
          {idx < 1 && <div style={{ flex: 1, height: '1px', background: step > n ? '#258ACB' : 'var(--border)', transition: 'background 0.4s' }} />}
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── IconInput helper ─────────────────────────────────────────────────────────
function IconInput({ icon, ...props }) {
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: '0.9rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }}>
        {icon}
      </div>
      <input
        {...props}
        style={{ ...inputBase, paddingLeft: '2.5rem' }}
        onFocus={e => e.target.style.borderColor = '#258ACB'}
        onBlur={e => e.target.style.borderColor = 'var(--border)'}
      />
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function Contact() {
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error

  // Step 1
  const [services,    setServices]    = useState([]);
  const [stage,       setStage]       = useState('');
  const [budget,      setBudget]      = useState('');
  const [timeline,    setTimeline]    = useState('');
  const [description, setDescription] = useState('');

  // Step 2
  const [name,    setName]    = useState('');
  const [email,   setEmail]   = useState('');
  const [phone,   setPhone]   = useState('');
  const [company, setCompany] = useState('');

  const toggleService = id => setServices(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const step1Valid = services.length > 0 && stage;

  const reset = () => {
    setStep(1); setStatus('idle');
    setServices([]); setStage(''); setBudget(''); setTimeline(''); setDescription('');
    setName(''); setEmail(''); setPhone(''); setCompany('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name || !email) return;

    setStatus('sending');

    const payload = {
      name, email, phone, company,
      services:    services.map(id => SERVICE_OPTIONS.find(o => o.id === id)?.label).filter(Boolean),
      stage:       STAGE_OPTIONS.find(o => o.id === stage)?.label    || stage,
      budget:      BUDGET_OPTIONS.find(o => o.id === budget)?.label  || budget,
      timeline:    TIMELINE_OPTIONS.find(o => o.id === timeline)?.label || timeline,
      description,
    };

    try {
      // Try EmailJS first
      if (EMAILJS_SERVICE_ID !== 'YOUR_SERVICE_ID') {
        await sendViaEmailJS(payload);
      } else {
        // Fallback to mailto if not configured
        mailtoFallback(payload);
      }
      setStatus('success');
    } catch (err) {
      console.error('EmailJS failed, falling back to mailto:', err);
      mailtoFallback(payload);
      setStatus('success');
    }
  };

  return (
    <section id="contact" style={{ padding: '7rem 0', position: 'relative', overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', width: '700px', height: '700px', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(37,138,203,0.09) 0%, transparent 70%)',
        top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
        filter: 'blur(60px)', pointerEvents: 'none',
      }} />

      <div className="container">
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="section-label" style={{ margin: '0 auto 1.5rem' }}>
            <span>◆</span> Start a Project
          </div>
          <h2 className="section-heading" style={{ margin: '0 auto 1rem' }}>
            Tell us about <span className="gradient-text">your project</span>
          </h2>
          <p className="section-subtext" style={{ margin: '0 auto' }}>
            Two steps. We read every brief personally and reply within 24 hours.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '4rem', maxWidth: '1040px', margin: '0 auto', alignItems: 'start' }} className="contact-layout">

          {/* ── Sidebar ── */}
          <div>
            <div style={{
              padding: '1.75rem', borderRadius: '16px',
              background: 'rgba(37,138,203,0.04)', border: '1px solid var(--border)',
              marginBottom: '1.5rem',
            }}>
              <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '1.25rem' }}>
                What happens next
              </div>
              {[
                { n: '01', t: 'We review your brief',  d: 'A senior engineer reads it personally.' },
                { n: '02', t: 'Free strategy call',    d: '30-min call to align on scope.' },
                { n: '03', t: 'Proposal in 48 hours',  d: 'Clear scope, timeline, fixed price.' },
              ].map(item => (
                <div key={item.n} style={{ display: 'flex', gap: '0.85rem', marginBottom: '1rem' }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                    background: 'rgba(37,138,203,0.12)', border: '1px solid rgba(37,138,203,0.28)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.62rem', fontWeight: 800, color: '#258ACB',
                  }}>{item.n}</div>
                  <div>
                    <div style={{ fontSize: '0.84rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '0.1rem' }}>{item.t}</div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>{item.d}</div>
                  </div>
                </div>
              ))}
            </div>

            {[
              { icon: <Mail size={14} />,        label: 'Email',         val: 'contact@nuageit.in' },
              { icon: <MessageSquare size={14} />,label: 'Response',     val: 'Within 24 hours'    },
            ].map(c => (
              <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', marginBottom: '0.7rem' }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '8px',
                  background: 'rgba(37,138,203,0.07)', border: '1px solid var(--border)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: '#258ACB', flexShrink: 0,
                }}>{c.icon}</div>
                <div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{c.label}</div>
                  <div style={{ fontSize: '0.84rem', fontWeight: 500 }}>{c.val}</div>
                </div>
              </div>
            ))}
          </div>

          {/* ── Form card ── */}
          <div style={{
            background: 'rgba(37,138,203,0.03)', border: '1px solid rgba(37,138,203,0.14)',
            borderRadius: '20px', padding: '2.5rem', backdropFilter: 'blur(12px)',
          }}>

            {/* SUCCESS */}
            {status === 'success' ? (
              <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
                <div style={{
                  width: 68, height: 68, borderRadius: '50%', margin: '0 auto 1.5rem',
                  background: 'rgba(16,185,129,0.1)', border: '2px solid rgba(16,185,129,0.35)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <CheckCircle size={34} color="#10B981" />
                </div>
                <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem', marginBottom: '0.75rem' }}>
                  Brief sent!
                </h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.92rem', lineHeight: 1.75, maxWidth: '340px', margin: '0 auto 1.75rem' }}>
                  We've received your project details at <strong style={{ color: '#3BA3E8' }}>contact@nuageit.in</strong> and will reply within 24 hours.
                </p>
                <button onClick={reset} className="btn-ghost" style={{ fontSize: '0.88rem' }}>
                  Submit another project
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <ProgressBar step={step} />

                {/* ── STEP 1 ── */}
                {step === 1 && (
                  <div style={{ animation: 'slideIn 0.3s ease' }}>

                    <div style={{ marginBottom: '1.75rem' }}>
                      <Label icon="🎯">What do you need built?</Label>
                      <Hint>Select everything that applies — we handle the full stack.</Hint>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.65rem' }}>
                        {SERVICE_OPTIONS.map(opt => (
                          <ServiceCard key={opt.id} opt={opt} selected={services.includes(opt.id)} onToggle={() => toggleService(opt.id)} />
                        ))}
                      </div>
                    </div>

                    <div style={{ marginBottom: '1.75rem' }}>
                      <Label icon="🚦">Where are you in the process?</Label>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.6rem', marginTop: '0.4rem' }}>
                        {STAGE_OPTIONS.map(opt => (
                          <StageCard key={opt.id} opt={opt} selected={stage === opt.id} onClick={() => setStage(opt.id)} />
                        ))}
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.75rem' }}>
                      <div>
                        <Label icon="💰">Budget range</Label>
                        <Hint>Helps us right-size the solution.</Hint>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                          {BUDGET_OPTIONS.map(o => <Chip key={o.id} selected={budget === o.id} onClick={() => setBudget(o.id)}>{o.label}</Chip>)}
                        </div>
                      </div>
                      <div>
                        <Label icon="📅">Timeline</Label>
                        <Hint>When do you need to launch?</Hint>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                          {TIMELINE_OPTIONS.map(o => <Chip key={o.id} selected={timeline === o.id} onClick={() => setTimeline(o.id)}>{o.label}</Chip>)}
                        </div>
                      </div>
                    </div>

                    <div style={{ marginBottom: '1.75rem' }}>
                      <Label icon="✍️">Describe your project</Label>
                      <Hint>The more detail, the more accurate our proposal.</Hint>
                      <textarea
                        value={description} onChange={e => setDescription(e.target.value)}
                        rows={5} placeholder={PLACEHOLDER}
                        style={{ ...inputBase, resize: 'vertical', lineHeight: 1.7 }}
                        onFocus={e => e.target.style.borderColor = '#258ACB'}
                        onBlur={e => e.target.style.borderColor = 'var(--border)'}
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => step1Valid && setStep(2)}
                      disabled={!step1Valid}
                      className="btn-primary"
                      style={{ width: '100%', justifyContent: 'center', padding: '1rem', opacity: step1Valid ? 1 : 0.4, cursor: step1Valid ? 'pointer' : 'not-allowed' }}
                    >
                      Continue to Contact Details <ArrowRight size={16} />
                    </button>

                    {!step1Valid && (
                      <p style={{ fontSize: '0.76rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '0.65rem' }}>
                        Pick at least one service and your project stage to continue.
                      </p>
                    )}
                  </div>
                )}

                {/* ── STEP 2 ── */}
                {step === 2 && (
                  <div style={{ animation: 'slideIn 0.3s ease' }}>

                    <div style={{ marginBottom: '1.5rem' }}>
                      <Label icon="👤">Your name <Req /></Label>
                      <IconInput icon={<User size={15} />} type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Sarah Mitchell" />
                    </div>

                    <div style={{ marginBottom: '1.5rem' }}>
                      <Label icon="📧">Work email <Req /></Label>
                      <IconInput icon={<Mail size={15} />} type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="sarah@company.com" />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
                      <div>
                        <Label icon="📞">Phone</Label>
                        <IconInput icon={<Phone size={15} />} type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+1 555 000 0000" />
                      </div>
                      <div>
                        <Label icon="🏢">Company</Label>
                        <IconInput icon={<Building2 size={15} />} type="text" value={company} onChange={e => setCompany(e.target.value)} placeholder="Acme Inc." />
                      </div>
                    </div>

                    {/* Summary */}
                    <div style={{
                      background: 'rgba(37,138,203,0.05)', border: '1px solid rgba(37,138,203,0.16)',
                      borderRadius: '12px', padding: '1rem 1.25rem', marginBottom: '1.5rem',
                    }}>
                      <div style={{ fontSize: '0.68rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: '0.65rem' }}>
                        Your brief summary
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                        {services.map(id => {
                          const o = SERVICE_OPTIONS.find(x => x.id === id);
                          return o ? (
                            <span key={id} style={{ fontSize: '0.76rem', fontWeight: 600, color: '#3BA3E8', background: 'rgba(37,138,203,0.1)', border: '1px solid rgba(37,138,203,0.25)', borderRadius: '100px', padding: '0.18rem 0.65rem' }}>
                              {o.label}
                            </span>
                          ) : null;
                        })}
                        {[
                          STAGE_OPTIONS.find(o => o.id === stage)?.label,
                          BUDGET_OPTIONS.find(o => o.id === budget)?.label,
                          TIMELINE_OPTIONS.find(o => o.id === timeline)?.label,
                        ].filter(Boolean).map(v => (
                          <span key={v} style={{ fontSize: '0.76rem', color: 'var(--text-muted)', background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border)', borderRadius: '100px', padding: '0.18rem 0.65rem' }}>{v}</span>
                        ))}
                      </div>
                    </div>

                    {/* Error banner */}
                    {status === 'error' && (
                      <div style={{
                        display: 'flex', gap: '0.6rem', alignItems: 'flex-start',
                        background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
                        borderRadius: '10px', padding: '0.85rem 1rem', marginBottom: '1.25rem',
                        fontSize: '0.82rem', color: '#fca5a5',
                      }}>
                        <AlertCircle size={16} style={{ flexShrink: 0, marginTop: '0.1rem' }} />
                        Something went wrong. We've opened your email client as a backup — just hit Send.
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                      <button type="button" onClick={() => setStep(1)} className="btn-ghost" style={{ padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.4rem', flexShrink: 0 }}>
                        <ArrowLeft size={15} /> Back
                      </button>
                      <button type="submit" disabled={status === 'sending'} className="btn-primary" style={{ flex: 1, justifyContent: 'center', padding: '1rem', opacity: status === 'sending' ? 0.7 : 1 }}>
                        {status === 'sending' ? (
                          <>
                            <span style={{ display: 'inline-block', width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: 'white', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
                            Sending…
                          </>
                        ) : (
                          <>Send Project Brief <ArrowRight size={16} /></>
                        )}
                      </button>
                    </div>

                    <p style={{ fontSize: '0.73rem', color: 'var(--text-muted)', textAlign: 'center', marginTop: '0.85rem', lineHeight: 1.6 }}>
                      Sent directly to <strong style={{ color: '#3BA3E8' }}>contact@nuageit.in</strong> via EmailJS. No spam, ever.
                    </p>
                  </div>
                )}
              </form>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 860px) { .contact-layout { grid-template-columns: 1fr !important; } }
        input::placeholder, textarea::placeholder { color: var(--text-muted); }
        @keyframes slideIn { from{opacity:0;transform:translateX(14px)} to{opacity:1;transform:none} }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </section>
  );
}